import DyteClient from '@dytesdk/web-core';
import React, { useContext, useEffect, useState } from 'react';
import { StyleSheet, TouchableHighlight, View } from 'react-native';

import { IconPack, defaultIconPack } from '../lib/icons';
import { States } from '../types/props';
import { DyteI18n, useLanguage } from '../lib/lang';
import DyteText from './DyteText';
import DyteParticipantTile from '../components/DyteParticipantTile';
import DyteMicToggle from './DyteMicToggle';
import DyteCameraToggle from './DyteCameraToggle';
import { useGridItemDimensions } from '../utils/grid';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function ({
  meeting,
  iconPack = defaultIconPack,
  states,
  t,
}: {
  meeting: DyteClient;
  iconPack?: IconPack;
  states?: States;
  t?: DyteI18n;
}) {
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  iconPack = iconPack ?? defaultIconPack;
  const { storeStates, setStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  states = states ?? storeStates;
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });
  useEffect(() => {
    const enableInit = async () => {
      await meeting.self.enableAudio();
      await meeting.self.enableVideo();
    };
    enableInit();
  }, [meeting]);
  const calcVals = useGridItemDimensions({
    count: 1,
    dimensions: {
      height: dimensions.height,
      width: dimensions.width,
    },
    aspectRatio: '3:4',
    gap: 4,
  });
  const styles = StyleSheet.create({
    controls: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-evenly',
      marginVertical: 10,
    },
    iconButtons: {
      padding: 1,
      margin: 2,
      marginHorizontal: 10,
      backgroundColor: colors.background[800],
      borderRadius: 10,
      height: 50,
      width: 50,
      justifyContent: 'center',
      alignItems: 'center',
    },
    buttonStyle: {
      borderRadius: 4,
      height: 40,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 16,
    },
  });
  return (
    <View
      onLayout={(event) => {
        setDimensions({
          width: event.nativeEvent.layout.width,
          height: event.nativeEvent.layout.height,
        });
      }}
    >
      <View
        // eslint-disable-next-line react-native/no-inline-styles
        style={{ alignItems: 'center' }}
      >
        <DyteParticipantTile
          participant={meeting.self}
          meeting={meeting}
          style={{ width: calcVals.width * 0.8, height: calcVals.height * 0.8 }}
        />
      </View>
      <View style={styles.controls}>
        <View style={styles.iconButtons}>
          <DyteMicToggle meeting={meeting} />
        </View>

        <View style={styles.iconButtons}>
          <DyteCameraToggle meeting={meeting} />
        </View>
      </View>
      <TouchableHighlight
        underlayColor={colors.background[800]}
        onPress={async () => {
          await meeting.stage.leave().catch(() => {
            setStates({ ...states, activeJoinStageConfirmation: false });
          });
          setStates({ ...states, activeJoinStageConfirmation: false });
        }}
        // eslint-disable-next-line react-native/no-inline-styles
        style={{ marginHorizontal: '6%', marginBottom: 8 }}
      >
        <View
          style={{
            ...styles.buttonStyle,
            backgroundColor: colors.danger,
          }}
        >
          <DyteText size="md">{t('stage.join_cancel')}</DyteText>
        </View>
      </TouchableHighlight>
      <TouchableHighlight
        underlayColor={colors.background[800]}
        onPress={async () => {
          await meeting.stage.join().catch(() => {
            setStates({ ...states, activeJoinStageConfirmation: false });
          });
          setStates({ ...states, activeJoinStageConfirmation: false });
        }}
        // eslint-disable-next-line react-native/no-inline-styles
        style={{ marginHorizontal: '6%' }}
      >
        <View
          style={{
            ...styles.buttonStyle,
            backgroundColor: colors.brand[500],
          }}
        >
          <DyteText size="md">{t('stage.join_confirm')}</DyteText>
        </View>
      </TouchableHighlight>
    </View>
  );
}
